import { useRef, useState } from "react";

export const useValidation = (message: string, regex?: RegExp, regexMessage?: string) => {
    const errorMessagesRef = useRef<Record<string, string | undefined>>({});
    const [_, r] = useState(0);
    const refresh = () => setTimeout(() => r(x => x + 1), 0);
    const ids = useRef(new Set<string>());

    const getErrorMessage = prepareGetErrorMessage(message, regex, regexMessage);

    const f = (id: string, props?: { value: string | undefined, onChange: (ev: React.ChangeEvent<HTMLInputElement> | string) => void }) => {
        ids.current.add(id);
        const newProps = {
            onBlur: (fe: React.FocusEvent<HTMLInputElement> | React.FocusEvent) => {
                errorMessagesRef.current = getErrorMessage((fe.target as HTMLInputElement).value, errorMessagesRef.current, id);
                refresh();
                if (fe.target.tagName == 'INPUT') {
                    const ev: React.ChangeEvent<HTMLInputElement> = {
                        nativeEvent: null!,
                        target: fe.target as HTMLInputElement,
                        currentTarget: fe.target as HTMLInputElement,
                        bubbles: false,
                        cancelable: false,
                        defaultPrevented: false,
                        eventPhase: 0,
                        isTrusted: true,
                        preventDefault: () => { },
                        isDefaultPrevented: () => false,
                        stopPropagation: () => { },
                        isPropagationStopped: () => false,
                        persist: () => { },
                        timeStamp: Date.now(),
                        type: 'custom'
                    };
                    props?.onChange?.(ev);
                } else {
                    props?.onChange?.((fe.target as HTMLInputElement).value);
                }
            },
            error: errorMessagesRef.current[id]
        };
        if (props) {
            return {
                ...newProps,
                ...props
            }
        }
        return newProps;
    }

    return Object.assign(f, {
        hasAnyErrors: () => Object.values(errorMessagesRef.current).some(e => !!e),
        revalidate: (data: Partial<Record<string, unknown>>) => {
            ids.current.forEach((name) => {
                errorMessagesRef.current = getErrorMessage(data[name] as string, errorMessagesRef.current, name);
            });
            refresh();
            return !Object.values(errorMessagesRef.current).some(e => !!e);
        }
    });
}

const prepareGetErrorMessage = (message: string, regex: RegExp | undefined, regexMessage: string | undefined) => (value: string | null | undefined, errorMessages: Record<string, string | undefined>, id: string) => {
    if (!value) {
        return ({
            ...errorMessages,
            [id]: message
        });
    } else if ((!!regex && !regex.test(value))) {
        return ({
            ...errorMessages,
            [id]: regexMessage ?? message
        });
    } else {
        return ({
            ...errorMessages,
            [id]: undefined
        });
    }
}